import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import PageHeader from "@/components/dashboard/shared/PageHeader";
import { KpiCard } from "@/components/dashboard/shared/KpiCard";
import { KpiGrid } from "@/components/dashboard/shared/KpiGrid";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AdminPageSkeleton } from "@/components/admin/AdminPageSkeleton";
import {
  Users,
  Search,
  ChevronLeft,
  ChevronRight,
  Calendar,
  UserCheck,
  Shield,
  Crown,
  User,
  Trash2,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/utils/api";
import { toast } from "sonner";

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
  onboardingStatus: string;
  plan?: "free" | "premium" | "custom" | "growth";
  nextPaymentDueAt?: string | null;
  nextPaymentInterval?: string | null;
  lastLoginAt?: string;
  createdAt: string;
  whatsappNumber?: string;
}

interface UserStats {
  totalUsers: number;
  roleStats: Record<string, number>;
  onboardingStats: Record<string, number>;
  planStats?: Record<string, number>;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalUsers: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export function UsersManagementPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [onboardingFilter, setOnboardingFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [planUpdatingId, setPlanUpdatingId] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [
    search,
    roleFilter,
    onboardingFilter,
    sortBy,
    sortOrder,
    currentPage,
    pageSize,
  ]);

  useEffect(() => {
    setSelectedUserIds([]);
  }, [currentPage, search, roleFilter, onboardingFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        sortBy,
        sortOrder,
      });

      if (search) params.append("search", search);
      if (roleFilter && roleFilter !== "all") params.append("role", roleFilter);
      if (onboardingFilter && onboardingFilter !== "all")
        params.append("onboardingStatus", onboardingFilter);

      const response = await api.get(
        `/api/platform-analytics/users/all?${params}`,
      );
      setUsers(response.data.users);
      setStats(response.data.stats);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "superadmin":
        return <Crown className="h-4 w-4" />;
      case "owner":
        return <Shield className="h-4 w-4" />;
      case "manager":
        return <UserCheck className="h-4 w-4" />;
      case "customer":
        return <User className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "superadmin":
        return "destructive";
      case "owner":
        return "default";
      case "manager":
        return "secondary";
      case "customer":
        return "outline";
      default:
        return "outline";
    }
  };

  const getOnboardingBadgeVariant = (status: string) => {
    switch (status) {
      case "completed":
        return "default";
      case "pending":
        return "secondary";
      case "business_info":
        return "outline";
      case "template_selection":
        return "outline";
      case "domain_setup":
        return "outline";
      default:
        return "outline";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const clearFilters = () => {
    setSearch("");
    setRoleFilter("all");
    setOnboardingFilter("all");
    setCurrentPage(1);
  };

  const deletableUsers = users.filter((u) => u.role !== "superadmin");
  const deletableUserIds = deletableUsers.map((u) => u._id);
  const allDeletableSelected =
    deletableUserIds.length > 0 &&
    deletableUserIds.every((id) => selectedUserIds.includes(id));

  const toggleSelectAll = (checked: boolean) => {
    setSelectedUserIds(checked ? deletableUserIds : []);
  };

  const toggleUserSelection = (userId: string, checked: boolean) => {
    setSelectedUserIds((ids) =>
      checked ? [...ids, userId] : ids.filter((id) => id !== userId),
    );
  };

  const bulkDeleteAccounts = async () => {
    if (selectedUserIds.length === 0) return;
    setBulkDeleting(true);
    try {
      const response = await api.post(
        "/api/platform-analytics/users/bulk-delete",
        { userIds: selectedUserIds },
      );
      const { deleted, failed } = response.data;
      if (deleted?.length) {
        toast.success(
          `Deleted ${deleted.length} account${deleted.length === 1 ? "" : "s"} and their store data`,
        );
      }
      if (failed?.length) {
        toast.error(
          `${failed.length} account${failed.length === 1 ? "" : "s"} could not be deleted`,
        );
      }
      setSelectedUserIds([]);
      await fetchUsers();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to delete accounts",
      );
    } finally {
      setBulkDeleting(false);
      setShowBulkDeleteDialog(false);
    }
  };

  const handlePlanChange = async (
    userId: string,
    newPlan: "free" | "premium" | "custom" | "growth",
  ) => {
    setPlanUpdatingId(userId);
    try {
      await api.patch(`/api/platform-analytics/users/${userId}/plan`, {
        plan: newPlan,
      });
      setUsers((prev) =>
        prev.map((u) => (u._id === userId ? { ...u, plan: newPlan } : u)),
      );
      await fetchUsers();
      toast.success("User plan updated successfully");
    } catch (error) {
      console.error("Error updating user plan:", error);
      toast.error("Failed to update user plan");
    } finally {
      setPlanUpdatingId(null);
    }
  };

  if (loading && !users.length) {
    return <AdminPageSkeleton kpiCount={5} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Users Management"
        subtitle="Manage all platform users and their details"
      />

      <KpiGrid loading={!stats} skeletonCount={5} columns="grid-cols-1 sm:grid-cols-2 xl:grid-cols-5">
        <KpiCard label="Total users" value={stats?.totalUsers ?? 0} icon={<Users />} />
        <KpiCard label="Store owners" value={stats?.roleStats.owner ?? 0} icon={<Shield />} />
        <KpiCard label="Managers" value={stats?.roleStats.manager ?? 0} icon={<UserCheck />} />
        <KpiCard
          label="Onboarded"
          value={stats?.onboardingStats.completed ?? 0}
          icon={<UserCheck />}
        />
        <KpiCard
          label="Paid plans"
          value={
            (stats?.planStats?.growth || 0) +
            (stats?.planStats?.premium || 0) +
            (stats?.planStats?.custom || 0)
          }
          icon={<Crown />}
        />
      </KpiGrid>

      {/* Users Table */}
      <Card className={dashboardStyles.tableWrap + " border-0 p-0"}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-wrap justify-between items-center mb-4">
            <div className="w-full max-w-[50%]">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="">
              <label className="text-sm font-medium">Onboarding Status</label>
              <Select
                value={onboardingFilter}
                onValueChange={setOnboardingFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="business_info">Business Info</SelectItem>
                  <SelectItem value="template_selection">
                    Template Selection
                  </SelectItem>
                  <SelectItem value="domain_setup">Domain Setup</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="">
              <label className="text-sm font-medium">Sort By</label>
              <Select
                value={`${sortBy}-${sortOrder}`}
                onValueChange={(value) => {
                  const [field, order] = value.split("-");
                  setSortBy(field);
                  setSortOrder(order);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt-desc">Newest First</SelectItem>
                  <SelectItem value="createdAt-asc">Oldest First</SelectItem>
                  <SelectItem value="name-asc">Name A-Z</SelectItem>
                  <SelectItem value="name-desc">Name Z-A</SelectItem>
                  <SelectItem value="lastLoginAt-desc">Last Login</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              onClick={clearFilters}
              size="sm"
              className="w-full sm:w-auto"
            >
              Clear Filters
            </Button>

            {selectedUserIds.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowBulkDeleteDialog(true)}
                className="w-full sm:w-auto"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete {selectedUserIds.length} selected
              </Button>
            )}
          </div>

          <div className="overflow-x-auto overflow-hidden rounded-[1.25rem] border border-[#EBEBEB]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allDeletableSelected}
                      disabled={deletableUserIds.length === 0}
                      onCheckedChange={(checked) =>
                        toggleSelectAll(checked === true)
                      }
                    />
                  </TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Onboarding</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedUserIds.includes(user._id)}
                        disabled={user.role === "superadmin"}
                        onCheckedChange={(checked) =>
                          toggleUserSelection(user._id, checked === true)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">
                          {user.name || "No name"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {user.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Select
                          value={user.plan || "free"}
                          disabled={planUpdatingId === user._id}
                          onValueChange={(v) =>
                            handlePlanChange(
                              user._id,
                              v as "free" | "premium" | "custom" | "growth",
                            )
                          }
                        >
                          <SelectTrigger className="w-[132px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="premium">Premium</SelectItem>
                            <SelectItem value="custom">Custom</SelectItem>
                            <SelectItem value="growth">
                              Growth (legacy)
                            </SelectItem>
                          </SelectContent>
                        </Select>

                        {user.plan === "premium" && user.nextPaymentDueAt && (
                          <p className="text-xs text-muted-foreground">
                            Next due:{" "}
                            {new Date(user.nextPaymentDueAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getOnboardingBadgeVariant(
                          user.onboardingStatus,
                        )}
                      >
                        {user.onboardingStatus}
                      </Badge>
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="h-3 w-3" />
                        {user.lastLoginAt
                          ? formatDate(user.lastLoginAt)
                          : "Never"}
                      </div>
                    </TableCell>

                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/users/${user._id}`)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                Page {pagination.currentPage} of {pagination.totalPages}
              </div>
              <div className="flex items-center justify-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrev}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNext}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
          <div className="text-xs text-muted-foreground text-center sm:text-right mt-4">
            Showing {users.length} of {pagination?.totalUsers || 0} users
          </div>
        </CardContent>
      </Card>

      <AlertDialog
        open={showBulkDeleteDialog}
        onOpenChange={setShowBulkDeleteDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedUserIds.length} account
              {selectedUserIds.length === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the selected users and all data for
              their owned stores, including products, orders, booking data, and
              customers tied to each account. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkDeleteAccounts}
              className="bg-red-600 hover:bg-red-700"
              disabled={bulkDeleting}
            >
              {bulkDeleting ? "Deleting..." : "Delete accounts"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
